import React, { useState, useEffect } from 'react';
import { LoginForm } from '@/components/auth/LoginForm';
import { SignUpForm } from '@/components/auth/SignUpForm';
import { PostSignupUpgrade } from '@/components/auth/PostSignupUpgrade';
import { useAuth } from '@/contexts/auth-context';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { WorkspaceService } from '@/services/workspace-service';
import { getWorkspaceUrl } from '@/utils/tenant-utils';

// Get the base domain from environment variable
const APP_DOMAIN = import.meta.env.VITE_APP_DOMAIN || 'wattfactory.tech';

export default function Auth() {
  const [searchParams] = useSearchParams();
  
  // Get invitation parameters first
  const invitationEmail = searchParams.get('email');
  const invitationId = searchParams.get('invitationId');
  const returnUrl = searchParams.get('returnUrl');
  
  // Allow signup for invitations or direct signup mode
  const isInvitationSignup = invitationEmail && invitationId;
  const isDirectSignup = searchParams.get('mode') === 'signup';
  const [isSignUp, setIsSignUp] = useState(isInvitationSignup || isDirectSignup);
  const [showPostSignupUpgrade, setShowPostSignupUpgrade] = useState(false);
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const toggleMode = () => {
    // Allow toggle for invitation signup or direct signup
    if (isInvitationSignup || isDirectSignup) {
      setIsSignUp(!isSignUp);
    }
  };

  // Handle authentication completion from email confirmation
  useEffect(() => {
    const handleAuthCompletion = async () => {
      // Check if we're coming from an email confirmation
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // User is authenticated, redirect appropriately
        const returnUrl = searchParams.get('returnUrl');
        console.log('🔍 Auth completion - returnUrl:', returnUrl);
        
        if (returnUrl) {
          try {
            const decodedUrl = decodeURIComponent(returnUrl);
            console.log('🔍 Auth completion decoded returnUrl:', decodedUrl);
            
            // Only immediately redirect for invitation URLs or workspace subdomains.
            // For localhost paths like /plan or /chat, defer to the next effect
            // that runs when `user` is available so we can build the workspace URL.
            if (decodedUrl.includes('/invite/')) {
              window.location.href = decodedUrl;
            } else if (
              decodedUrl.includes('://') &&
              APP_DOMAIN &&
              decodedUrl.includes(APP_DOMAIN) &&
              !decodedUrl.includes('www.')
            ) {
              window.location.href = decodedUrl;
            }
          } catch (error) {
            console.error('Invalid return URL:', error);
            // Do nothing here; the next effect will route when `user` is set
          }
        }
      }
    };

    // Only run this if we're not loading and don't have a user yet
    if (!loading && !user) {
      handleAuthCompletion();
    }
  }, [loading, user, navigate, searchParams]);

  // Check for intended plan after successful signup
  useEffect(() => {
    if (!loading && user) {
      const intendedPlan = sessionStorage.getItem('intendedPlan');
      if (intendedPlan && (intendedPlan === 'basic' || intendedPlan === 'premium')) {
        setShowPostSignupUpgrade(true);
        return;
      }
    }
  }, [user, loading]);

  // Redirect to returnUrl or home if user is already logged in
  useEffect(() => {
    const handleRedirect = async () => {
      if (!loading && user && !showPostSignupUpgrade) {
        const returnUrl = searchParams.get('returnUrl');
        console.log('🔍 Auth redirect check - returnUrl:', returnUrl);
        
        if (returnUrl) {
          // Decode the return URL and check if it's an invitation
          try {
            const decodedUrl = decodeURIComponent(returnUrl);
            console.log('🔍 Decoded returnUrl:', decodedUrl);
            
            // Only redirect to returnUrl for invitation URLs or workspace subdomains
            if (decodedUrl.includes('/invite/')) {
              // For invitation URLs, redirect directly to the invitation
              window.location.href = decodedUrl;
              return;
            }
            
            // Allow workspace subdomain redirects (but not central domain to prevent loops)
            console.log('🔍 DEBUG: Checking redirect condition...');
            console.log('🔍 DEBUG: decodedUrl.includes("://") =', decodedUrl.includes('://'));
            console.log('🔍 DEBUG: APP_DOMAIN =', APP_DOMAIN);
            console.log('🔍 DEBUG: decodedUrl.includes(APP_DOMAIN) =', APP_DOMAIN ? decodedUrl.includes(APP_DOMAIN) : 'APP_DOMAIN is undefined');
            console.log('🔍 DEBUG: !decodedUrl.includes("www.") =', !decodedUrl.includes('www.'));
            
            if (decodedUrl.includes('://') && APP_DOMAIN && decodedUrl.includes(APP_DOMAIN) && !decodedUrl.includes('www.')) {
              console.log('🏢 Redirecting to workspace subdomain:', decodedUrl);
              console.log('🔍 DEBUG: APP_DOMAIN =', APP_DOMAIN);
              window.location.href = decodedUrl;
              return;
            }
            
            // For workspace routes (like /plan, /chat), redirect to workspace context
            if (decodedUrl.includes('/plan') || decodedUrl.includes('/chat')) {
              console.log('🏢 Redirecting workspace route to workspace context:', decodedUrl);
              // Get the user's default workspace and redirect to workspace URL
              const userWorkspaces = await WorkspaceService.getUserWorkspaces();
              if (userWorkspaces.length > 0) {
                const defaultWorkspace = userWorkspaces[0]; // Use first workspace as default
                // Ensure we pass only path + query to the workspace URL builder
                let pathAndQuery = decodedUrl;
                try {
                  const urlObj = new URL(decodedUrl, window.location.origin);
                  pathAndQuery = `${urlObj.pathname}${urlObj.search}`;
                } catch (_) {
                  // If it's already a path, keep as-is
                }
                const workspaceUrl = getWorkspaceUrl(defaultWorkspace.slug, pathAndQuery);
                console.log('🏢 Redirecting to workspace URL:', workspaceUrl);
                window.location.href = workspaceUrl;
                return;
              }
              // If user has no workspaces yet, fall through to home where onboarding handles creation
            }
            
            // For all other URLs, ignore returnUrl and go to home
            console.log('🏠 Ignoring returnUrl, going to home for proper workspace flow');
            console.log('🔍 DEBUG: APP_DOMAIN =', APP_DOMAIN, 'decodedUrl =', decodedUrl);
            navigate('/');
          } catch (error) {
            console.error('Invalid return URL:', error);
            navigate('/');
          }
        } else {
          console.log('✅ No returnUrl, going to home');
          navigate('/');
        }
      }
    };

    handleRedirect();
  }, [user, loading, navigate, searchParams, showPostSignupUpgrade]);

  // Show loading while checking authentication status
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Show post-signup upgrade if user is logged in and has intended plan
  if (user && showPostSignupUpgrade) {
    const intendedPlan = sessionStorage.getItem('intendedPlan');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
        <PostSignupUpgrade
          intendedPlan={intendedPlan || undefined}
          workspaceId={user.tenant_id || undefined}
          workspaceSlug={user.tenant_id || undefined} // You might need to get the actual slug
          userEmail={user.email}
        />
      </div>
    );
  }

  // Don't render the auth forms if user is logged in (will redirect)
  if (user) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <div className="w-full max-w-md">
        {isSignUp ? (
          <SignUpForm onToggleMode={toggleMode} />
        ) : (
          <LoginForm onToggleMode={toggleMode} />
        )}
      </div>
    </div>
  );
} 